import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGym } from '../contexts/GymContext';
import { revenueAPI, expenseAPI } from '../services/api';
import api from '../services/api';
import { DashboardLayout } from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Plus, 
  Trash2, 
  Pencil, 
  TrendingUp, 
  TrendingDown,
  DollarSign,
  ArrowUpCircle,
  ArrowDownCircle,
  ArrowRight,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';

const MESES = [
  'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
  'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'
];

const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value || 0);
};

export default function DREPage() {
  const { selectedGym } = useGym();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  
  // Dados
  const [revenues, setRevenues] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [fluxoDados, setFluxoDados] = useState([]);
  
  // Filtros
  const [filtroMes, setFiltroMes] = useState(MESES[new Date().getMonth()]);
  const [filtroAno, setFiltroAno] = useState(new Date().getFullYear());
  
  // Modais
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  
  // Formulários
  const [expenseForm, setExpenseForm] = useState({
    category: '',
    amount: '',
    description: '',
    is_operacional: true
  });
  
  // Expandir/Colapsar seções
  const [expandReceitas, setExpandReceitas] = useState(true);
  const [expandDespesasOp, setExpandDespesasOp] = useState(true);
  const [expandDespesasNaoOp, setExpandDespesasNaoOp] = useState(true);
  const [naoOpCaixa, setNaoOpCaixa] = useState({ items: [], total: 0 });

  const loadData = useCallback(async () => {
    if (!selectedGym?.id) return;
    setLoading(true);
    try {
      const [revenuesRes, expensesRes, categoriesRes] = await Promise.all([
        revenueAPI.getAll(selectedGym.id),
        expenseAPI.getAll(selectedGym.id),
        expenseAPI.getCategories(selectedGym.id)
      ]);
      setRevenues(revenuesRes.data);
      setExpenses(expensesRes.data);
      setExpenseCategories(categoriesRes.data);
      try {
        const fluxoRes = await api.get(`/fluxo-caixa-completo?gym_id=${selectedGym.id}&meses=36`);
        setFluxoDados(fluxoRes.data?.fluxo_mensal || []);
      } catch (e) { setFluxoDados([]); }
    } catch (error) {
      toast.error('Erro ao carregar dados do DRE');
    } finally {
      setLoading(false);
    }
  }, [selectedGym?.id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Buscar despesas não-operacionais do Caixa (pontual + investimento) para o mês/ano filtrado
  useEffect(() => {
    const fetchNaoOp = async () => {
      if (!selectedGym?.id) return;
      try {
        const res = await api.get(`/dre/despesas-nao-operacionais?gym_id=${selectedGym.id}&mes=${encodeURIComponent(filtroMes)}&ano=${filtroAno}`);
        setNaoOpCaixa(res.data || { items: [], total: 0 });
      } catch (e) {
        setNaoOpCaixa({ items: [], total: 0 });
      }
    };
    fetchNaoOp();
  }, [selectedGym?.id, filtroMes, filtroAno]);

  // Filtrar dados pelo mês/ano
  const revenuesFiltradas = revenues.filter(r => 
    r.month?.toLowerCase() === filtroMes.toLowerCase() && r.year === filtroAno
  );
  
  const expensesFiltradas = expenses.filter(e => 
    e.month?.toLowerCase() === filtroMes.toLowerCase() && e.year === filtroAno
  );
  
  // Separar despesas operacionais e não operacionais
  const despesasOperacionais = expensesFiltradas.filter(e => {
    // Verificar is_operacional do expense ou da categoria
    if (e.is_operacional !== undefined) return e.is_operacional;
    const cat = expenseCategories.find(c => c.name === e.category);
    return cat?.is_operacional !== false;
  });
  
  const despesasNaoOperacionais = expensesFiltradas.filter(e => {
    if (e.is_operacional !== undefined) return !e.is_operacional;
    const cat = expenseCategories.find(c => c.name === e.category);
    return cat?.is_operacional === false;
  });
  
  // Despesas não-op vindas do CAIXA (pontual + investimento importados via OFX/manual no Caixa)
  const despesasNaoOpCaixaMapped = (naoOpCaixa.items || []).map(i => ({
    id: i.id,
    description: i.descricao,
    category: i.categoria,
    amount: i.valor,
    tipo: i.tipo,
    _origem: 'caixa'
  }));
  const despesasNaoOperacionaisTodas = [...despesasNaoOperacionais, ...despesasNaoOpCaixaMapped];
  
  // Totais
  const totalReceitas = revenuesFiltradas.reduce((sum, r) => sum + (r.amount || 0), 0);
  const totalDespesasOp = despesasOperacionais.reduce((sum, e) => sum + (e.amount || 0), 0);
  const totalDespesasNaoOp = despesasNaoOperacionaisTodas.reduce((sum, e) => sum + (e.amount || 0), 0);
  const totalDespesas = totalDespesasOp + totalDespesasNaoOp; // total geral (informativo)
  // RESULTADO DO MÊS = OPERACIONAL (receitas − despesas operacionais).
  // As despesas NÃO operacionais são apenas INFORMATIVAS (não reduzem o resultado operacional).
  // A visão geral fica na "Margem de Lucro Geral" (do Fluxo/caixa realizado) logo abaixo.
  const resultado = totalReceitas - totalDespesasOp;
  const margemLiquida = totalReceitas > 0 ? ((resultado / totalReceitas) * 100).toFixed(1) : 0;

  // Lucro Líquido Geral e Margem Geral (mesma fórmula do Fluxo / caixa realizado)
  const fluxoMesAtual = fluxoDados.find(f => 
    (f.mes || '').toLowerCase() === filtroMes.toLowerCase() && f.ano === filtroAno
  );
  const lucroLiquidoGeral = fluxoMesAtual ? fluxoMesAtual.lucro_liquido : null;
  const margemGeral = fluxoMesAtual ? fluxoMesAtual.margem_lucro_pct : null;

  // Agrupar receitas por fonte
  const receitasPorFonte = revenuesFiltradas.reduce((acc, r) => {
    const fonte = r.source_type || r.description || 'Outros';
    if (!acc[fonte]) acc[fonte] = 0;
    acc[fonte] += r.amount || 0;
    return acc;
  }, {});

  // Agrupar despesas por categoria
  const despesasOpPorCategoria = despesasOperacionais.reduce((acc, e) => {
    const cat = e.category || 'Outros';
    if (!acc[cat]) acc[cat] = 0;
    acc[cat] += e.amount || 0;
    return acc;
  }, {});

  const despesasNaoOpPorCategoria = despesasNaoOperacionais.reduce((acc, e) => {
    const cat = e.category || 'Outros';
    if (!acc[cat]) acc[cat] = 0;
    acc[cat] += e.amount || 0;
    return acc;
  }, {});

  // Handlers de Receita (somente leitura na DRE - cadastro/edição feitos em Gestão de Receita)
  const handleDeleteRevenue = async (id) => {
    if (!window.confirm('Excluir esta receita?')) return;
    try {
      await revenueAPI.delete(id);
      toast.success('Receita excluída');
      loadData();
    } catch (error) {
      toast.error('Erro ao excluir');
    }
  };

  // Handlers de Despesa
  const handleSaveExpense = async () => {
    if (!expenseForm.amount || !expenseForm.category) {
      toast.error('Informe categoria e valor');
      return;
    }
    try {
      const categoria = expenseCategories.find(c => c.name === expenseForm.category);
      const payload = {
        gym_id: selectedGym.id,
        month: filtroMes,
        year: filtroAno,
        category: expenseForm.category,
        amount: parseFloat(expenseForm.amount),
        description: expenseForm.description || expenseForm.category,
        is_operacional: categoria?.is_operacional !== false
      };
      
      if (editingExpense) {
        await expenseAPI.update(editingExpense.id, payload);
        toast.success('Despesa atualizada');
      } else {
        await expenseAPI.create(payload);
        toast.success('Despesa cadastrada');
      }
      setShowExpenseModal(false);
      setEditingExpense(null);
      resetExpenseForm();
      loadData();
    } catch (error) {
      toast.error('Erro ao salvar despesa');
    }
  };

  const handleEditExpense = (expense) => {
    setEditingExpense(expense);
    setExpenseForm({
      category: expense.category || '',
      amount: expense.amount?.toString() || '',
      description: expense.description || '',
      is_operacional: expense.is_operacional !== false
    });
    setShowExpenseModal(true);
  };

  const handleDeleteExpense = async (id) => {
    if (!window.confirm('Excluir esta despesa?')) return;
    try {
      await expenseAPI.delete(id);
      toast.success('Despesa excluída');
      loadData();
    } catch (error) {
      toast.error('Erro ao excluir');
    }
  };

  const resetExpenseForm = () => {
    setExpenseForm({ category: '', amount: '', description: '', is_operacional: true });
  };

  if (!selectedGym) {
    return (
      <DashboardLayout>
        <div>
          <Card style={{ backgroundColor: 'var(--app-card-bg)', borderColor: 'var(--app-border)' }}>
            <CardContent className="p-8 text-center">
              <DollarSign className="w-12 h-12 mx-auto mb-4 opacity-50" style={{ color: 'var(--color-primary)' }} />
              <p style={{ color: 'var(--app-text-secondary)' }}>Selecione uma empresa para ver o DRE</p>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 overflow-x-hidden">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold" style={{ color: 'var(--app-text-primary)' }}>
              DRE - Demonstração do Resultado
            </h1>
            <p className="text-sm mt-1" style={{ color: 'var(--app-text-secondary)' }}>
              Receitas e despesas por competência (o que pertence ao mês)
            </p>
          </div>
          
          {/* Filtros */}
          <div className="flex gap-2">
            <Select value={filtroMes} onValueChange={setFiltroMes}>
              <SelectTrigger className="w-[140px]" style={{ backgroundColor: 'var(--app-background)', borderColor: 'var(--app-border)', color: 'var(--app-text-primary)' }}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent style={{ backgroundColor: 'var(--app-card-bg)', borderColor: 'var(--app-border)' }}>
                {MESES.map((m) => (
                  <SelectItem key={m} value={m}>{m.charAt(0).toUpperCase() + m.slice(1)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={filtroAno.toString()} onValueChange={(v) => setFiltroAno(parseInt(v))}>
              <SelectTrigger className="w-[100px]" style={{ backgroundColor: 'var(--app-background)', borderColor: 'var(--app-border)', color: 'var(--app-text-primary)' }}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent style={{ backgroundColor: 'var(--app-card-bg)', borderColor: 'var(--app-border)' }}>
                {[2023, 2024, 2025, 2026].map((y) => (
                  <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Card Principal do DRE */}
        <Card style={{ backgroundColor: 'var(--app-card-bg)', borderColor: 'var(--app-border)' }}>
          <CardContent className="p-4 md:p-6">
            {loading ? (
              <div className="text-center py-8" style={{ color: 'var(--app-text-secondary)' }}>
                Carregando...
              </div>
            ) : (
              <div className="space-y-4">
                {/* RECEITAS */}
                <div>
                  <div 
                    className="flex items-center justify-between cursor-pointer p-3 rounded-lg hover:opacity-80 transition-opacity"
                    style={{ backgroundColor: 'rgba(34, 197, 94, 0.1)' }}
                    onClick={() => setExpandReceitas(!expandReceitas)}
                  >
                    <div className="flex items-center gap-2">
                      <ArrowUpCircle size={20} className="text-green-500" />
                      <span className="font-semibold text-green-500">RECEITAS</span>
                      {expandReceitas ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-bold text-lg text-green-500">{formatCurrency(totalReceitas)}</span>
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={(e) => { e.stopPropagation(); navigate('/gestao-receita'); }}
                        className="text-green-500 hover:text-green-600 gap-1"
                        data-testid="dre-ir-gestao-receita-button"
                      >
                        <span className="text-xs hidden sm:inline">Gestão de Receita</span>
                        <ArrowRight size={16} />
                      </Button>
                    </div>
                  </div>
                  
                  {expandReceitas && (
                    <div className="mt-2 ml-6 space-y-1">
                      <p className="text-xs pb-1" style={{ color: 'var(--app-text-muted)' }}>
                        Receitas são cadastradas em Gestão de Receita. Aqui você só visualiza e pode excluir.
                      </p>
                      {revenuesFiltradas.length === 0 ? (
                        <p className="text-sm py-2" style={{ color: 'var(--app-text-muted)' }}>
                          Nenhuma receita cadastrada
                        </p>
                      ) : (
                        revenuesFiltradas.map((rev) => (
                          <div key={rev.id} className="flex items-center justify-between py-2 px-2 rounded hover:bg-white/5 group">
                            <div className="flex-1">
                              <span className="text-sm" style={{ color: 'var(--app-text-secondary)' }}>
                                {rev.description || rev.source_type || 'Receita'}
                              </span>
                              {rev.source_type && (
                                <span className="text-xs ml-2 px-2 py-0.5 rounded" style={{ backgroundColor: 'var(--app-border)', color: 'var(--app-text-muted)' }}>
                                  {rev.source_type}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-green-500">
                                {formatCurrency(rev.amount)}
                              </span>
                              <div className="flex gap-1 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  className="h-6 w-6 p-0 text-red-500"
                                  onClick={(e) => { e.stopPropagation(); handleDeleteRevenue(rev.id); }}
                                  data-testid={`delete-revenue-${rev.id}`}
                                >
                                  <Trash2 size={12} />
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>

                {/* Linha divisória */}
                <div className="border-t" style={{ borderColor: 'var(--app-border)' }} />

                {/* DESPESAS OPERACIONAIS */}
                <div>
                  <div 
                    className="flex items-center justify-between cursor-pointer p-3 rounded-lg hover:opacity-80 transition-opacity"
                    style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)' }}
                    onClick={() => setExpandDespesasOp(!expandDespesasOp)}
                  >
                    <div className="flex items-center gap-2">
                      <ArrowDownCircle size={20} className="text-red-500" />
                      <span className="font-semibold text-red-500">DESPESAS OPERACIONAIS</span>
                      {expandDespesasOp ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-bold text-lg text-red-500">-{formatCurrency(totalDespesasOp)}</span>
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={(e) => { e.stopPropagation(); setShowExpenseModal(true); }}
                        className="text-red-500 hover:text-red-600"
                        data-testid="add-expense-btn"
                      >
                        <Plus size={16} />
                      </Button>
                    </div>
                  </div>
                  
                  {expandDespesasOp && (
                    <div className="mt-2 ml-6 space-y-1">
                      {despesasOperacionais.length === 0 ? (
                        <p className="text-sm py-2" style={{ color: 'var(--app-text-muted)' }}>
                          Nenhuma despesa operacional
                        </p>
                      ) : (
                        despesasOperacionais.map((expense) => (
                          <div key={expense.id} className="flex items-center justify-between py-2 px-2 rounded hover:bg-white/5 group">
                            <div className="flex-1">
                              <span className="text-sm" style={{ color: 'var(--app-text-secondary)' }}>
                                {expense.description || expense.category}
                              </span>
                              <span className="text-xs ml-2 px-2 py-0.5 rounded" style={{ backgroundColor: 'var(--app-border)', color: 'var(--app-text-muted)' }}>
                                {expense.category}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium" style={{ color: 'var(--app-text-primary)' }}>
                                {formatCurrency(expense.amount)}
                              </span>
                              <div className="flex gap-1 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  className="h-6 w-6 p-0"
                                  onClick={(e) => { e.stopPropagation(); handleEditExpense(expense); }}
                                  data-testid={`edit-expense-${expense.id}`}
                                >
                                  <Pencil size={12} />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  className="h-6 w-6 p-0 text-red-500"
                                  onClick={(e) => { e.stopPropagation(); handleDeleteExpense(expense.id); }}
                                  data-testid={`delete-expense-${expense.id}`}
                                >
                                  <Trash2 size={12} />
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>

                {/* DESPESAS NÃO OPERACIONAIS */}
                <div>
                  <div 
                    className="flex items-center justify-between cursor-pointer p-3 rounded-lg hover:opacity-80 transition-opacity"
                    style={{ backgroundColor: 'rgba(249, 115, 22, 0.1)' }}
                    onClick={() => setExpandDespesasNaoOp(!expandDespesasNaoOp)}
                  >
                    <div className="flex items-center gap-2">
                      <ArrowDownCircle size={20} className="text-orange-500" />
                      <span className="font-semibold text-orange-500">DESPESAS NÃO OPERACIONAIS</span>
                      <span className="text-[11px] px-2 py-0.5 rounded bg-orange-500/15 text-orange-300">informativo</span>
                      {expandDespesasNaoOp ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </div>
                    <span className="font-bold text-lg text-orange-500">-{formatCurrency(totalDespesasNaoOp)}</span>
                  </div>
                  
                  {expandDespesasNaoOp && (
                    <div className="mt-2 ml-6 space-y-1">
                      {despesasNaoOperacionaisTodas.length === 0 ? (
                        <p className="text-sm py-2" style={{ color: 'var(--app-text-muted)' }}>
                          Nenhuma despesa não operacional
                        </p>
                      ) : (
                        despesasNaoOperacionaisTodas.map((expense) => (
                          <div key={expense.id} className="flex items-center justify-between py-2 px-2 rounded hover:bg-white/5 group">
                            <div className="flex-1">
                              <span className="text-sm" style={{ color: 'var(--app-text-secondary)' }}>
                                {expense.description || expense.category}
                              </span>
                              <span className="text-xs ml-2 px-2 py-0.5 rounded" style={{ backgroundColor: 'var(--app-border)', color: 'var(--app-text-muted)' }}>
                                {expense.category}
                              </span>
                              {expense._origem === 'caixa' && (
                                <span className="text-xs ml-1 px-2 py-0.5 rounded bg-blue-500/15 text-blue-400" title="Importado do Caixa (OFX)">
                                  {expense.tipo === 'investimento' ? 'Investimento' : 'Pontual'} · Caixa
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium" style={{ color: 'var(--app-text-primary)' }}>
                                {formatCurrency(expense.amount)}
                              </span>
                              {expense._origem !== 'caixa' && (
                                <div className="flex gap-1 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    className="h-6 w-6 p-0"
                                    onClick={(e) => { e.stopPropagation(); handleEditExpense(expense); }}
                                    data-testid={`edit-expense-naoOp-${expense.id}`}
                                  >
                                    <Pencil size={12} />
                                  </Button>
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    className="h-6 w-6 p-0 text-red-500"
                                    onClick={(e) => { e.stopPropagation(); handleDeleteExpense(expense.id); }}
                                    data-testid={`delete-expense-naoOp-${expense.id}`}
                                  >
                                    <Trash2 size={12} />
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>

                {/* Linha divisória grossa */}
                <div className="border-t-2" style={{ borderColor: 'var(--color-primary)' }} />

                {/* RESULTADO */}
                <div 
                  className="p-4 rounded-lg"
                  style={{ backgroundColor: resultado >= 0 ? 'rgba(34, 197, 94, 0.15)' : 'rgba(239, 68, 68, 0.15)' }}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-bold text-lg" style={{ color: 'var(--app-text-primary)' }}>
                        RESULTADO OPERACIONAL DO MÊS
                      </span>
                      <p className="text-sm" style={{ color: 'var(--app-text-muted)' }}>
                        (Receitas − Despesas Operacionais) · não-operacionais são só informativas
                      </p>
                    </div>
                    <div className="text-right">
                      <span className={`font-bold text-2xl ${resultado >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                        {formatCurrency(resultado)}
                      </span>
                      <p className={`text-sm ${resultado >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                        Margem: {margemLiquida}%
                      </p>
                    </div>
                  </div>
                </div>

                {/* LUCRO LÍQUIDO GERAL / MARGEM GERAL (mesma fórmula do Fluxo - caixa realizado) */}
                <div className="p-4 rounded-lg border" style={{ borderColor: 'var(--app-border)', backgroundColor: 'var(--app-background)' }} data-testid="dre-margem-geral">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-semibold text-base" style={{ color: 'var(--app-text-primary)' }}>
                        Margem de Lucro Geral do mês
                      </span>
                      <p className="text-xs" style={{ color: 'var(--app-text-muted)' }}>
                        Do Fluxo (caixa realizado): Entradas − (Saídas Op + Pontual + Investimento) ÷ Entradas
                      </p>
                    </div>
                    <div className="text-right">
                      {margemGeral !== null && margemGeral !== undefined ? (
                        <>
                          <span className={`font-bold text-2xl ${(lucroLiquidoGeral || 0) >= 0 ? 'text-green-500' : 'text-red-500'}`} data-testid="dre-margem-geral-pct">
                            {margemGeral}%
                          </span>
                          <p className={`text-sm ${(lucroLiquidoGeral || 0) >= 0 ? 'text-green-500' : 'text-red-500'}`} data-testid="dre-lucro-liquido-geral">
                            Lucro Líquido: {formatCurrency(lucroLiquidoGeral)}
                          </p>
                        </>
                      ) : (
                        <span className="text-sm" style={{ color: 'var(--app-text-muted)' }}>Sem movimentação de caixa neste mês</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Modal Adicionar Despesa */}
        <Dialog open={showExpenseModal} onOpenChange={(open) => {
          setShowExpenseModal(open);
          if (!open) { setEditingExpense(null); resetExpenseForm(); }
        }}>
          <DialogContent className="max-h-[90vh] overflow-y-auto" style={{ backgroundColor: 'var(--app-card-bg)', borderColor: 'var(--app-border)' }}>
            <DialogHeader>
              <DialogTitle style={{ color: 'var(--app-text-primary)' }}>
                {editingExpense ? 'Editar Despesa' : 'Nova Despesa'} - {filtroMes}/{filtroAno}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label style={{ color: 'var(--app-text-secondary)' }}>Categoria *</Label>
                <Select value={expenseForm.category} onValueChange={(v) => setExpenseForm({ ...expenseForm, category: v })}>
                  <SelectTrigger style={{ backgroundColor: 'var(--app-background)', borderColor: 'var(--app-border)', color: 'var(--app-text-primary)' }}>
                    <SelectValue placeholder="Selecione a categoria" />
                  </SelectTrigger>
                  <SelectContent style={{ backgroundColor: 'var(--app-card-bg)', borderColor: 'var(--app-border)' }}>
                    {expenseCategories.map((cat) => (
                      <SelectItem key={cat.id || cat.name} value={cat.name}>
                        {cat.name} {cat.is_operacional === false && '(Não Op.)'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs mt-1" style={{ color: 'var(--app-text-muted)' }}>
                  Configure categorias em Configurações
                </p>
              </div>
              
              <div>
                <Label style={{ color: 'var(--app-text-secondary)' }}>Valor (R$) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={expenseForm.amount}
                  onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })}
                  placeholder="0,00"
                  style={{ backgroundColor: 'var(--app-background)', borderColor: 'var(--app-border)', color: 'var(--app-text-primary)' }}
                />
              </div>
              
              <div>
                <Label style={{ color: 'var(--app-text-secondary)' }}>Descrição (opcional)</Label>
                <Input
                  value={expenseForm.description}
                  onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })}
                  placeholder="Ex: Folha de pagamento"
                  style={{ backgroundColor: 'var(--app-background)', borderColor: 'var(--app-border)', color: 'var(--app-text-primary)' }}
                />
              </div>
              
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => { setShowExpenseModal(false); setEditingExpense(null); resetExpenseForm(); }}>
                  Cancelar
                </Button>
                <Button onClick={handleSaveExpense} style={{ backgroundColor: 'var(--color-primary)' }}>
                  {editingExpense ? 'Salvar' : 'Cadastrar'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
